// src/pages/Grow/Intakes/Components/ApproveIntakeModal.tsx
/**
 * Approving a course intake: creates the `Registration`, its participant members, and optionally
 * assigns the facilitators, all in one `approve()` call.
 *
 * ⚠️ **This is irreversible.** There is no un-approve endpoint, and approval matches or creates member
 * records for every participant. The safeguards are therefore in the wizard itself rather than in where
 * it can be opened from: the member matches get a step of their own, the Review step restates every
 * choice, the close button is locked while busy, and the final button states how many people are being
 * enrolled so it cannot be pressed on autopilot.
 *
 * ⓘ It opens from the queue's row action as well as the detail page. That is safe **because** of the
 * above — an earlier single-modal version was detail-page-only precisely because it lacked them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Card, Group, Modal, Stack, Text, Textarea,
} from '@mantine/core';
import {
  IconAlertTriangle, IconCalendar, IconCheck, IconInfoCircle, IconUserCheck, IconUsers,
} from '@tabler/icons-react';
import { Select } from '@/components';
import MultiStepForm, { type FormStep } from '@/components/advanced/forms/MultiStepForm';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useACCourseService, useRegistrationIntakeService,
  type CourseDateModelView, type MemberCandidateQuery, type ParticipantMemberChoice,
  type RegistrationIntakeModelView,
} from '@actschurch/ccms-sdk';
import MemberMatchPicker, { type MemberChoice } from '@/components/common/MemberMatchPicker';
import FacilitatorPicker from './FacilitatorPicker';

export interface ApproveIntakeModalProps {
  intake: RegistrationIntakeModelView | null;
  opened: boolean;
  onClose: () => void;
  /** Called with the new registration id once it exists, so the caller can offer a link to it. */
  onApproved: (registrationID: number | null) => void;
}

export default function ApproveIntakeModal({
  intake, opened, onClose, onApproved,
}: ApproveIntakeModalProps) {
  const intakeService = useRegistrationIntakeService();
  const courseService = useACCourseService();

  const [dateID, setDateID] = useState<string | null>(null);
  const [facilitatorIds, setFacilitatorIds] = useState<string[]>([]);
  const [leadUserId, setLeadUserId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [dates, setDates] = useState<CourseDateModelView[]>([]);
  /**
   * Per-participant member resolution, keyed by `participantID`.
   *
   * ⚠️ Absent or `null` means "untouched", and untouched participants are **left out of the payload**
   * so approval behaves exactly as it did before this picker existed.
   */
  const [memberChoices, setMemberChoices] = useState<Record<number, MemberChoice>>({});

  // Reset per intake, and prefill the date the applicant actually chose.
  useEffect(() => {
    if (!opened || !intake) return;
    setFacilitatorIds([]);
    setLeadUserId(null);
    setNote('');
    setMemberChoices({});
    setDateID(intake.dateID != null ? String(intake.dateID) : null);
  }, [opened, intake]);

  useEffect(() => {
    if (!opened || !intake) return;
    courseService.getDates(intake.courseID)
      .then((list) => setDates(Array.isArray(list) ? list : []))
      .catch((e) => console.error('[ApproveIntake] failed to load course dates:', e));
  }, [opened, intake, courseService]);

  const fetchCandidates = useCallback(
    (query: MemberCandidateQuery) => intakeService.candidateMatches(query),
    [intakeService],
  );

  const participants = useMemo(() => intake?.participants ?? [], [intake]);

  /** Only the participants the admin actually resolved. Empty array → omit the field entirely. */
  const participantMembers = useMemo<ParticipantMemberChoice[]>(
    () => participants.flatMap((p) => {
      const choice = memberChoices[p.participantID];
      if (!choice) return [];
      return [{ participantID: p.participantID, ...choice }];
    }),
    [participants, memberChoices],
  );

  const handleApprove = async () => {
    if (!intake || busy) return;
    setBusy(true);
    try {
      const res = await intakeService.approve(intake.intakeID, {
        dateID: dateID ? Number(dateID) : null,
        facilitatorUserIds: facilitatorIds.length > 0 ? facilitatorIds : null,
        leadFacilitatorUserId: leadUserId ?? null,
        note: note.trim() || null,
        // ⚠️ `null`, not `[]`, when nothing was picked — an empty array could read as "resolve nobody".
        participantMembers: participantMembers.length > 0 ? participantMembers : null,
      });

      // ⚠️ Read through the envelope. The method is typed `Promise<Registration>`, but this API returns
      // `ResponseData`-wrapped payloads in several places, and navigating to `/Registration/undefined`
      // on a miss is worse than not offering the link.
      type CreatedRegistration = { registrationID?: number };
      const envelope = res as unknown as { data?: CreatedRegistration };
      const created = (envelope?.data ?? res) as CreatedRegistration;
      const registrationID = typeof created?.registrationID === 'number' ? created.registrationID : null;

      onApproved(registrationID);
      onClose();
    } catch (e) {
      console.error('[ApproveIntake] approve failed:', e);
      toast.error('Could not approve', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const participantCount = intake?.participants?.length ?? 0;
  const dateChanged = intake != null && dateID !== null && Number(dateID) !== intake.dateID;

  const dateOptions = dates.map((d) => ({
    value: String(d.dateID),
    label: [d.periodDisplay ?? d.startDisplay ?? `Date ${d.dateID}`, d.locationName]
      .filter(Boolean).join(' · '),
  }));

  /**
   * The approval split into steps.
   *
   * ⚠️ One long modal made the irreversible bits easy to skim past — the member matches in particular,
   * which decide whether a duplicate person is created. Each step now asks one question, and the
   * summary restates every choice before the button that cannot be undone.
   */
  const steps: FormStep[] = intake ? [
    {
      title: 'People',
      description: participants.length === 1 ? '1 person' : `${participants.length} people`,
      icon: <IconUsers size={18} />,
      content: (
        <Stack gap="md">
          <div>
            <Text size="sm" fw={600}>
              {participants.length === 1 ? 'Who is enrolling' : `${participants.length} people on this intake`}
            </Text>
            <Text size="xs" c="dimmed">
              Each becomes a registration member on approval. Leave a person on{' '}
              <Text span fw={600}>Match automatically</Text> unless you can see they already exist
              under a different email or phone.
            </Text>
          </div>

          {participants.length === 0 ? (
            <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
              <Text size="sm">
                No separate participants were captured, so the applicant is the only person enrolling.
              </Text>
            </Alert>
          ) : (
            <Stack gap="xs">
              {participants.map((p) => (
                <MemberMatchPicker
                  key={p.participantID}
                  personName={[
                    p.fullName || [p.name, p.surname].filter(Boolean).join(' ').trim() || 'Unnamed',
                    p.isMain ? '(main)' : '',
                  ].filter(Boolean).join(' ')}
                  query={{
                    firstName: p.name,
                    lastName: p.surname,
                    email: p.email,
                    phone: p.phone,
                  }}
                  fetchCandidates={fetchCandidates}
                  value={memberChoices[p.participantID] ?? null}
                  onChange={(choice) => setMemberChoices((prev) => ({
                    ...prev,
                    [p.participantID]: choice,
                  }))}
                />
              ))}
            </Stack>
          )}
        </Stack>
      ),
    },
    {
      title: 'Course date',
      description: dateChanged ? 'Changed' : 'As signed up',
      icon: <IconCalendar size={18} />,
      content: (
        <Stack gap="sm">
          <Text size="sm" fw={600}>Which intake date?</Text>
          <Select
            placeholder="Keep the date the applicant chose"
            data={dateOptions}
            value={dateID}
            onChange={setDateID}
            searchable
            clearable
          />
          {dateChanged && (
            <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">
                This moves the applicant off the date they signed up for. Let them know.
              </Text>
            </Alert>
          )}
        </Stack>
      ),
    },
    {
      title: 'Facilitators',
      description: facilitatorIds.length > 0 ? `${facilitatorIds.length} selected` : 'Optional',
      icon: <IconUserCheck size={18} />,
      content: (
        <Stack gap="sm">
          <div>
            <Text size="sm" fw={600}>Assign facilitators</Text>
            <Text size="xs" c="dimmed">
              Optional. Starts with the facilitators who already cover this course. The first one picked
              becomes the lead, and you can move the star.
            </Text>
          </div>
          <FacilitatorPicker
            courseID={intake.courseID}
            value={facilitatorIds}
            onChange={setFacilitatorIds}
            leadUserId={leadUserId}
            onLeadChange={setLeadUserId}
          />
        </Stack>
      ),
    },
    {
      title: 'Review',
      description: 'Confirm and enrol',
      icon: <IconCheck size={18} />,
      content: (
        <Stack gap="md">
          {/* ⚠️ Restates every choice. Approval creates member records and cannot be undone, so the
              last screen before it should not require remembering three steps back. */}
          <Card withBorder radius="md" padding="md">
            <Stack gap={6}>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Course</Text>
                <Text size="sm" fw={600}>{intake.courseName ?? '—'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Enrolling</Text>
                <Text size="sm" fw={600}>
                  {participants.length > 0
                    ? `${participants.length} ${participants.length === 1 ? 'person' : 'people'}`
                    : 'the applicant'}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Intake date</Text>
                <Text size="sm" fw={600}>
                  {dateOptions.find((d) => d.value === dateID)?.label
                    ?? 'As the applicant chose'}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Facilitators</Text>
                <Text size="sm" fw={600}>
                  {facilitatorIds.length > 0 ? `${facilitatorIds.length} assigned` : 'None'}
                </Text>
              </Group>
            </Stack>
          </Card>

          <Textarea
            label="Note"
            description="Anything the facilitators should know before making contact. Optional."
            autosize
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />

          {facilitatorIds.length === 0 && (
            <Alert color="gray" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">
                No facilitator selected. The registration will be created without one, ready to be
                assigned later.
              </Text>
            </Alert>
          )}
        </Stack>
      ),
    },
  ] : [];

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title="Approve intake and create registration"
      size="xl"
      centered
    >
      {intake && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {intake.reference ?? `Intake #${intake.intakeID}`} from{' '}
            <Text span fw={600}>{intake.fullName ?? 'Unknown'}</Text> for{' '}
            <Text span fw={600}>{intake.courseName ?? 'a course'}</Text>.
          </Text>

          <MultiStepForm
            steps={steps}
            onComplete={handleApprove}
            onCancel={onClose}
            loading={busy}
            completeLabel={participantCount > 1
              ? `Approve and enrol ${participantCount} people`
              : 'Approve and create registration'}
          />
        </Stack>
      )}
    </Modal>
  );
}
